import { Splide } from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

/**
 * KVデフォルト：Splide 初期化処理
 * @param {string} selector - Splideを初期化する要素のセレクタ
 * @param {Object} options - Splideのオプション設定
 * @param {Object} extensions - Splideの拡張機能（必要に応じて）
 */
const initializeKvDefaultSplide = () => {
  const defaultSelector = '#visual .js-splide-fade .splide';
  const defaultOptions = {
    lazyLoad: 'nearby',
    type: 'fade',
    rewind: true,
    autoplay: true,
    pauseOnHover: false,
    pauseOnFocus: false,
    arrows: false,
    interval: 7000,
    speed: 2000,
  };

  const defaultSplide = new Splide(defaultSelector, defaultOptions);

  // 不要なrole属性を削除する監視設定
  const removeRoleAttributes = () => {
    const splideList = document.querySelector(`${defaultSelector} .splide__list`);
    if (splideList && splideList.hasAttribute('aria-roledescription')) {
      splideList.removeAttribute('aria-roledescription');
      splideList.removeAttribute('role');
    }
  };

  // MutationObserverを使用して初期化時の属性変化を監視
  const observer = new MutationObserver(removeRoleAttributes);

  defaultSplide.on('mounted', () => {
    const splideList = document.querySelector(`${defaultSelector} .splide__list`);
    if (splideList) {
      observer.observe(splideList, { attributes: true });
    }
  });

  defaultSplide.mount();
};

/**
 * KVナビゲーション付き：Splide 初期化処理（メイン＆サムネイル）
 */
const initializeKvMainAndThumbSliders = () => {
  const thumbSelector = '#visual .js-splide-navigation .splide';
  const thumbOptions = {
    mediaQuery: 'min',
    lazyLoad: 'nearby',
    gap: 0,
    arrows: false,
    drag: false,
    pagination: false,
    isNavigation: true,
    direction: 'ttb',
    heightRatio: 1,
    perPage: 4,
    breakpoints: {
      600: {
        gap: 8,
        heightRatio: 1.2,
      },
      991: {
        gap: 8,
        heightRatio: 1.2,
      },
      1200: {
        direction: 'ltr',
        fixedHeight: '200px',
        gap: 16,
      },
    },
  };

  const thumbSplide = new Splide(thumbSelector, thumbOptions);

  // 初期化後に不要な role を削除
  const observer = new MutationObserver(() => {
    const splideList = document.querySelector(`${thumbSelector} .splide__list`);
    if (splideList && splideList.hasAttribute('aria-orientation')) {
      splideList.removeAttribute('aria-orientation');
      splideList.removeAttribute('role');
    }
  });

  thumbSplide.on('mounted', () => {
    const splideList = document.querySelector(`${thumbSelector} .splide__list`);
    if (splideList) {
      observer.observe(splideList, { attributes: true });
    }
  });

  const mainSelector = '#visual .splide-main';
  const mainOptions = {
    lazyLoad: 'nearby',
    type: 'loop',
    autoplay: true,
    updateOnMove: true,
    speed: 2000,
    pagination: false,
    arrows: false,
    drag: false,
    height: 800,
    breakpoints: {
      1024: {
        height: 'clamp(400px,70vh, 600px)',
      },
    },
  };

  const mainSplide = new Splide(mainSelector, mainOptions);

  // サムネイルとメインを同期
  mainSplide.sync(thumbSplide);

  // Splide インスタンスをマウント
  mainSplide.mount();
  thumbSplide.mount();
};

/**
 * ティッカースライダーの初期化
 */
const initializeTickerSlider = () => {
  const tickerSelector = '.js-ticker .splide';

  if (document.querySelector(tickerSelector)) {
    const tickerOptions = {
      lazyLoad: 'nearby',
      mediaQuery: 'min',
      fixedWidth: '15rem',
      gap: 16,
      type: 'loop',
      arrows: false,
      drag: 'free',
      flickPower: 300,
      pagination: false,
      autoScroll: {
        speed: 0.5,
        pauseOnHover: false,
        pauseOnFocus: false,
      },
      breakpoints: {
        1025: {
          gap: 32,
          fixedWidth: '25rem',
        },
      },
    };

    const tickerSplide = new Splide(tickerSelector, tickerOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    });

    tickerSplide.on('mounted', () => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    // Splide Extensions を使用してマウント
    tickerSplide.mount({ AutoScroll });
  }
};

/**
 * 通常スライダーの初期化
 */
const initializeSlider = () => {
  const sliderSelector = '.js-slider .splide';

  if (document.querySelector(sliderSelector)) {
    const sliderOptions = {
      type: 'fade',
      rewind: true,
    };

    const sliderSplide = new Splide(sliderSelector, sliderOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${sliderSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    });

    sliderSplide.on('mounted', () => {
      const splideList = document.querySelector(`${sliderSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    // Splide Extensions を使用してマウント
    sliderSplide.mount();
  }
};

/**
 * Splide 全体の初期化
 */
const initializeSplide = () => {
  // TOPページのみで初期化
  if (window.location.pathname === '/') {
    initializeKvDefaultSplide();
  }
  initializeTickerSlider();
  initializeSlider();
};

/**
 * モジュールの初期化
 */
const init = () => {
  initializeSplide();
};

export default { init };
